import type { DockviewApi, Parameters as DockviewParameters } from "dockview"
import type { DimensionValue, OPDockviewPanelDefinition, PanelDimensionRange } from "./types"

interface ContainerDimensions {
  width: number
  height: number
}

type ResolveDimensionInput = { unit: "pixel", value: number } | { unit: "percent", value: string, containerSize: number }

const resolveDimensionValue = (input: ResolveDimensionInput): number => {
  if (input.unit === "pixel") return input.value
  return Math.round((input.containerSize * parseFloat(input.value)) / 100)
}

const resolveDimensionRange = (range: PanelDimensionRange | undefined, containerSize: number): { min?: number, max?: number } => {
  if (!range) return {}

  const resolveValue = (dimension: DimensionValue | undefined): number | undefined => {
    if (!dimension) return undefined
    if (typeof dimension === "number") return resolveDimensionValue({ unit: "pixel", value: dimension })
    return resolveDimensionValue({ unit: "percent", value: dimension, containerSize })
  }

  return {
    min: resolveValue(range.min),
    max: resolveValue(range.max),
  }
}

/** Disconnects MutationObserver if the tab node never appears (avoids a leaked observer). */
const SCROLL_TAB_INTO_VIEW_SAFETY_MS = 4000

function getDockviewComponentElement(api: DockviewApi): HTMLElement | undefined {
  return (api as unknown as { component?: { element: HTMLElement } }).component?.element
}

function getPanelTabSearchRoot(api: DockviewApi, panelId: string): Document | HTMLElement {
  const groupElement = api.getPanel(panelId)?.group?.element
  if (groupElement) return groupElement

  const dockviewElement = getDockviewComponentElement(api)
  if (dockviewElement) return dockviewElement

  return document
}

function getPanelTabElement(api: DockviewApi, panelId: string): HTMLElement | null {
  const root = getPanelTabSearchRoot(api, panelId)
  return root.querySelector<HTMLElement>(`[data-panel-id="${CSS.escape(panelId)}"]`)
}

/**
 * Scrolls the tab for the given panel into view in the tabs strip, accounting for the
 * .dv-right-actions-container width so the tab is not hidden under it.
 */
function tryScrollPanelTabIntoViewOnce(api: DockviewApi, panelId: string): boolean {
  const tab = getPanelTabElement(api, panelId)
  if (!tab) {
    return false
  }
  tab.scrollIntoView({ block: "nearest", inline: "end", behavior: "smooth" })
  return true
}

function runDeferredScrollPanelTabIntoView(api: DockviewApi, panelId: string): void {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (tryScrollPanelTabIntoViewOnce(api, panelId)) {
        return
      }

      const root = getPanelTabSearchRoot(api, panelId)
      const observeTarget = root instanceof Document ? document.body : root
      let cleanupTimer: number | undefined
      const observer = new MutationObserver(() => {
        if (tryScrollPanelTabIntoViewOnce(api, panelId)) {
          observer.disconnect()
          if (cleanupTimer !== undefined) {
            window.clearTimeout(cleanupTimer)
          }
        }
      })
      observer.observe(observeTarget, { childList: true, subtree: true })

      cleanupTimer = window.setTimeout(() => {
        cleanupTimer = undefined
        observer.disconnect()
      }, SCROLL_TAB_INTO_VIEW_SAFETY_MS)

      if (tryScrollPanelTabIntoViewOnce(api, panelId)) {
        if (cleanupTimer !== undefined) {
          window.clearTimeout(cleanupTimer)
        }
        observer.disconnect()
      }
    })
  })
}

function subscribeScrollPanelTabIntoViewWhenPanelAdded(api: DockviewApi, panelId: string): { dispose: () => void } {
  const subscription = api.onDidAddPanel((panel) => {
    if (panel.id !== panelId) {
      return
    }
    subscription.dispose()
    runDeferredScrollPanelTabIntoView(api, panelId)
  })
  return subscription
}

export function resolvePanelDefinition<P extends DockviewParameters>(
  panelDefinition: OPDockviewPanelDefinition<P>,
  containerDimensions: ContainerDimensions,
): OPDockviewPanelDefinition<P> {
  const { min: minimumWidth, max: maximumWidth } = resolveDimensionRange(panelDefinition.widthRange, containerDimensions.width)
  const { min: minimumHeight, max: maximumHeight } = resolveDimensionRange(panelDefinition.heightRange, containerDimensions.height)

  return {
    ...panelDefinition,
    minimumWidth,
    maximumWidth,
    minimumHeight,
    maximumHeight,
  }
}

/**
 * Applies a complete set of panel definitions by clearing the layout and recreating all panels.
 * Use this for initial layout creation, manual resets, and error recovery.
 * For incremental updates, use applyPanelChanges instead.
 *
 * @param api - Dockview API instance
 * @param panels - Complete set of panel definitions to apply
 */
export function applyPanelDefinitions(api: DockviewApi, panels: readonly OPDockviewPanelDefinition[]): void {
  api.clear()

  const containerDimensions: ContainerDimensions = {
    width: api.width,
    height: api.height,
  }

  panels.forEach((panelDefinition, index) => {
    const resolvedDefinition = resolvePanelDefinition(panelDefinition, containerDimensions)
    const panel = api.addPanel(resolvedDefinition)

    if (index === 0) {
      panel.api.setActive()
    }
  })
}

/**
 * Panel changes for incremental updates
 */
export interface PanelChanges {
  /** Panel definitions to add */
  added: OPDockviewPanelDefinition[]
  /** Panel IDs to remove */
  removed: string[]
}

/**
 * Applies incremental panel changes without destroying the existing layout.
 * Preserves user's layout customizations while adding/removing specific panels.
 *
 * Strategy for adding panels:
 * - If panels exist: Stack new panels as tabs with the first panel
 * - If no panels exist: Add as the first panel
 *
 * @param api - Dockview API instance
 * @param changes - Panel additions and removals to apply
 *
 */
export function applyPanelChanges(
  api: DockviewApi,
  changes: PanelChanges,
): void {
  const { added, removed } = changes

  // Remove panels that no longer exist
  removed.forEach(panelId => {
    const panel = api.getPanel(panelId)
    if (panel) {
      api.removePanel(panel)
    }
  })

  // Add new panels without clearing existing ones
  const containerDimensions: ContainerDimensions = {
    width: api.width,
    height: api.height,
  }

  // Capture reference panel before loop to ensure consistent grouping.
  // Prefer active panel so new tabs appear next to the current tab (dv-tabs-container).
  // (api.panels may update synchronously during addPanel calls)
  const referencePanel = api.activePanel ?? api.panels[0]

  const panelsToAdd = added.filter(panelDef => !api.getPanel(panelDef.id))
  const lastAddedPanelId = panelsToAdd.length > 0 ? panelsToAdd[panelsToAdd.length - 1].id : null

  let scrollSubscription: { dispose: () => void } | undefined
  if (lastAddedPanelId) {
    scrollSubscription = subscribeScrollPanelTabIntoViewWhenPanelAdded(api, lastAddedPanelId)
  }

  try {
    panelsToAdd.forEach(panelDef => {
      const resolved = resolvePanelDefinition(panelDef, containerDimensions)

      // Strategy: Stack new panels with reference panel as tabs (same tab strip)
      if (referencePanel) {
        api.addPanel({
          ...resolved,
          position: {
            referencePanel: referencePanel.id,
            direction: "within" as const,
          },
          floating: false,
        })
      } else {
        api.addPanel(resolved)
      }
    })
  } finally {
    scrollSubscription?.dispose()
  }
}
